import React from 'react';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import usePagination, { UsePaginationItem } from '@mui/material/usePagination';

import BotPager from './BotPager/BotPager';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'components/UI/Pagination/Pagination';
import { getUri } from 'services/generic';
import {
  convertTypeToDetail,
  pushPagerChangeEvent,
} from 'services/listing/gtm';
import { isBot } from 'services/ua';
import { MOBILE } from 'settings/mediaQuery';
import { PagerType } from 'types/ProductListing/ProductListing';

import styles from './Pager.module.scss';

type PropsType = { pager: PagerType; classes?: { container?: string } };

export default function Pager({ pager, classes }: PropsType): JSX.Element {
  return isBot() ? (
    <BotPager pager={pager} />
  ) : (
    <ClientPager pager={pager} classes={classes} />
  );
}

function ClientPager({ pager, classes }: PropsType): JSX.Element {
  const isMobile = useMediaQuery({ query: MOBILE });
  const { currentPageNumber, lastPageNumber, pages } = pager;
  const { items } = usePagination({
    count: lastPageNumber,
    page: currentPageNumber,
    siblingCount: isMobile ? 1 : 4,
    boundaryCount: 1,
  });

  function getPageUrl(pageNumber: number | null) {
    const pageUrl = pageNumber ? pages[pageNumber] || '' : '';

    return pageUrl.length ? getUri(pageUrl) : '#';
  }

  function renderItem({
    type,
    page,
    disabled,
    selected,
    onClick,
  }: UsePaginationItem) {
    if (type === 'start-ellipsis' || type === 'end-ellipsis') {
      return (
        <PaginationEllipsis
          className={classNames(styles.itemRoot, styles.itemEllipsis)}
        />
      );
    }

    function handleClick(
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      data?: string
    ) {
      if (data) pushPagerChangeEvent(convertTypeToDetail(data));
      onClick(e);
    }

    if (type === 'previous') {
      return (
        <PaginationPrevious
          to={getPageUrl(page)}
          aria-disabled={disabled}
          onClick={e => handleClick(e, type)}
          className={classNames(
            styles.itemRoot,
            styles.itemIcon,
            disabled && styles.disabled
          )}
        />
      );
    }

    if (type === 'next') {
      return (
        <PaginationNext
          to={getPageUrl(page)}
          aria-disabled={disabled}
          onClick={e => handleClick(e, type)}
          className={classNames(
            styles.itemRoot,
            styles.itemIcon,
            disabled && styles.disabled
          )}
        />
      );
    }

    return (
      <PaginationLink
        to={getPageUrl(page)}
        onClick={e => handleClick(e, page?.toString())}
        className={classNames(styles.itemRoot, selected && styles.itemSelected)}
      >
        {page}
      </PaginationLink>
    );
  }

  return (
    <Pagination className={classNames(styles.container, classes?.container)}>
      <PaginationContent className={styles.list}>
        {items.map((item, index) => (
          <PaginationItem key={index}>{renderItem(item)}</PaginationItem>
        ))}
      </PaginationContent>
    </Pagination>
  );
}
